import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Dialog,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import { NOTE_TYPE_COLOURS } from 'src/components/Constants';
import { getRecordDate, getRecordTitle } from './forms/formService';
import RecordBrowser from './RecordBrowser';

// TGCLOUD-1669 Phase 4 — the dialog around the record browser.
//
// Everything that made this a BROWSER moved to RecordBrowser: the facets, the
// search, the card list, the paging and all of their state. What is left is
// what makes it a DIALOG: the window, the title bar naming the patient, the
// sort control that lives in that title bar, printing, and closing.
//
// Printing is here rather than in the browser because it prints the DIALOG's
// idea of the record set: the patient label in the heading is dialog state.
// It needs the browser's filtered list and expanded set, which arrive through
// `onVisibleChange` and are held in a ref — deliberately not state, or every
// keystroke in the browser's search box would re-render the whole dialog.

const RecordBrowserModal = ({
  open,
  onClose,
  tiles = [],
  isLoading,
  patientName,
  patientId,
  patientDob,
  staffMemberOptions = [],
  // The member whose records these are — only used to resolve a PRENATAL event's subject label
  // (decision 21), so a fetal test never reads as something done to the patient.
  carrier,
  // Offered when the person has NO records at all.
  onCreateRecord,
  creatableTypes = []
}) => {
  const { theme } = React.useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  // The sort control sits in this dialog's header, so this dialog owns the
  // value and hands it down. Reset on open, alongside the browser's own reset.
  const [sortOrder, setSortOrder] = useState('newest');
  useEffect(() => {
    if (open) setSortOrder('newest');
  }, [open]);

  const visibleRef = useRef({ filteredRecords: [], expandedIds: new Set() });
  const handleVisibleChange = useCallback((next) => {
    visibleRef.current = next;
  }, []);

  const patientLabel = [
    patientId || null,
    patientName || null,
    patientDob && moment(patientDob).isValid()
      ? moment(patientDob).format(dateFormat)
      : null
  ]
    .filter(Boolean)
    .join(' - ');

  const handlePrint = () => {
    const { filteredRecords, expandedIds } = visibleRef.current;
    const win = window.open('', '_blank');
    if (!win) return;

    const fmt = (d) =>
      d && moment(d).isValid() ? moment(d).format(dateFormat) : '';
    const findStaff = (id) =>
      staffMemberOptions?.find((s) => s.value === id)?.label || id;
    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const row = (label, value, pre) => {
      if (value == null || value === '') return '';
      return `<tr><td class="lbl">${esc(label)}</td><td${
        pre ? ' class="pre"' : ''
      }>${esc(value)}</td></tr>`;
    };

    const getDetailsHtml = (r) => {
      let rows = '';
      switch (r.noteType) {
        case 'Appointment':
          rows += row('Date', fmt(r.appointmentDate));
          rows += r.appointmentTime
            ? row('Time', moment(r.appointmentTime).format('HH:mm'))
            : '';
          rows += row('Clinic', r.clinicName);
          rows += row('Type', r.appointmentType);
          rows += row('Status', r.appointmentStatus);
          rows += row('Location', r.location);
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += row('Notes', r.appointmentNotes, true);
          break;
        case 'Referral':
          rows += row('Date', fmt(r.referralDate));
          rows += row('Type', r.referralType);
          rows += row('Referred To', r.referralTo);
          rows += row('Referred By', r.referredBy);
          rows += row('Status', r.referralStatus);
          rows += row('Triage', r.referralTriage);
          rows += row('Reason', r.referralReason, true);
          rows += row('Notes', r.referralNotes, true);
          rows += row('Date Closed', fmt(r.dateClosed));
          rows += row('Reason for Closure', r.reasonForClosure, true);
          break;
        case 'Test':
          rows += row('Test Name', r.testName);
          rows += row('Type', r.testType);
          rows += row('Date Requested', fmt(r.dateRequested));
          rows += row('Date Due', fmt(r.dateResultDue));
          rows += row('Date Received', fmt(r.dateResultReceived));
          rows += row('Lab', r.lab);
          rows +=
            r.ageWhenTested != null
              ? row('Age When Tested', r.ageWhenTested)
              : '';
          rows += row('Result', r.testResult, true);
          rows += row('Notes', r.testNotes, true);
          break;
        case 'Sample':
          rows += row('Date Taken', fmt(r.dateTaken));
          rows += row('Type', r.sampleType);
          rows += row('Laboratory', r.laboratory);
          rows += row('Status', r.sampleStatus);
          rows += row('Status Date', fmt(r.statusDate));
          rows += row('Held By', r.sampleHeldBy);
          rows += row('Tracking ID', r.trackingId);
          break;
        case 'Clinical Note':
          rows += row('Date', fmt(r.clinicalNoteDate));
          rows += row('Clinician', r.clinician);
          rows += row('Note', r.note, true);
          break;
        case 'Contact':
          rows += row('Date', fmt(r.contactDate));
          rows += row('Type', r.contactType);
          rows += row('Mode', r.contactMode);
          rows += row('Status', r.contactStatus);
          rows += row('Reason', r.reasonForContact, true);
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += row('Notes', r.contactNotes, true);
          break;
        case 'Consent':
          rows += row('Date', fmt(r.consentDate || r.consentRequestDate));
          rows +=
            r.consentRequestDate && r.consentDate
              ? row('Request Date', fmt(r.consentRequestDate))
              : '';
          rows += row('Type', r.consentType);
          rows += row('Response', r.response);
          rows += row('Given By', r.consentGivenBy);
          rows += row('Expiry', fmt(r.expiryDate));
          rows += row('Notes', r.consentNotes, true);
          break;
        case 'Treatment':
          rows += row('Start Date', fmt(r.treatmentDate));
          rows += row('End Date', fmt(r.treatmentEndDate));
          rows += row('Treatment', r.treatment, true);
          rows += row('Comment', r.comment, true);
          break;
        case 'Address':
          rows += row('Line 1', r.addressLine1);
          rows += row('Line 2', r.addressLine2);
          rows += row('Town/City', r.town || r.city);
          rows += row('Postcode', r.postCode);
          rows += row('Country', r.country);
          rows += r.primaryAddress ? row('Primary', 'Yes') : '';
          rows += r.correspondenceAddress ? row('Correspondence', 'Yes') : '';
          break;
        case 'Tissue Pathology':
          rows += row('Tissue Site', r.tissueSite);
          rows += row('Morphology', r.morphology);
          rows += row('Report Date', fmt(r.pathReportDate));
          rows += r.staffMember?.length
            ? row('Staff', r.staffMember.map(findStaff).join(', '))
            : '';
          rows += r.pathologyDoctor
            ? row('Pathologist', findStaff(r.pathologyDoctor))
            : '';
          break;
        case 'Disease':
          rows += row('ID', r.id);
          rows += row('Age at diagnosis', r.age);
          rows += row('Date reported', fmt(r.dateReported));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Phenotype':
          rows += row('ID', r.id);
          rows += row('Age reported', r.recordedAge);
          rows += row('Date recorded', fmt(r.dateRecorded));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Genotype':
          rows += row('ID', r.id);
          rows += row('Carrier status', r.carrierStatus);
          rows += row('Age reported', r.recordedAge);
          rows += row('Date recorded', fmt(r.dateRecorded));
          rows += row(
            'Clinically confirmed',
            r.clinicallyConfirmed ? 'Yes' : 'No'
          );
          break;
        case 'Pregnancy': {
          const outcomeLabels = {
            ongoing: 'Ongoing',
            sab: 'Miscarriage (SAB)',
            ectopic: 'Ectopic (ECT)',
            top: 'Termination (TOP)',
            stillbirth: 'Stillbirth (SB)',
            liveBirth: 'Live birth'
          };
          const conceivedLabels = {
            partner: 'Partner',
            spermDonor: 'Sperm donor',
            eggDonor: 'Egg donor',
            embryoDonor: 'Donated embryo',
            partnerAndDonor: 'Partner + donor'
          };
          rows += row('Outcome', outcomeLabels[r.outcome] || r.outcome);
          rows +=
            r.gestationalWeeks != null && r.gestationalWeeks !== ''
              ? row('Gestational age', `${r.gestationalWeeks} weeks`)
              : '';
          rows += row(
            'Conceived with',
            conceivedLabels[r.conceivedWith] || r.conceivedWith
          );
          rows +=
            r.fetalSexKnown && r.fetalSex
              ? row('Fetal sex', r.fetalSex === 'male' ? 'Male' : 'Female')
              : '';
          rows += row('Karyotype / finding', r.karyotype);
          rows += row('Note', r.comments, true);
          rows += r.hidden ? row('Visibility', 'Hidden from pedigree') : '';
          break;
        }
        case 'Risk Score':
          rows += row('Type', r.riskLabel);
          if (r.recordType === 'gail' && r.gailRiskScore != null)
            rows += row('Risk score', r.gailRiskScore);
          if (r.recordType === 'canrisk') {
            try {
              const pr = r.canriskResult?.pedigree_result?.[0];
              const tenYr =
                pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
              const lifetime =
                pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
              if (tenYr != null && lifetime != null) {
                rows += row(
                  'Result',
                  `10yr: ${tenYr}% | Lifetime: ${lifetime}%`
                );
              }
            } catch (_) {
              /* ignore */
            }
          }
          if (r.recordType === 'prs' && r.prsResult) {
            rows += row('Trait', r.prsResult.trait || r.prsResult.pgsId);
            if (r.prsResult.zscore != null)
              rows += row('Z-score', r.prsResult.zscore);
            if (r.prsResult.percentile != null)
              rows += row('Percentile', `${r.prsResult.percentile}%`);
          }
          break;
        default:
          break;
      }
      if (!rows) return '';
      return `<table class="details"><tbody>${rows}</tbody></table>`;
    };

    const cards = filteredRecords
      .map((r) => {
        const d = getRecordDate(r);
        const date = fmt(d);
        const typeColor = NOTE_TYPE_COLOURS[r.noteType] || '#888';
        const isExpanded = expandedIds.has(r._id);
        const details = isExpanded ? getDetailsHtml(r) : '';
        return `<div class="card${
          isExpanded ? ' expanded' : ''
        }" style="border-left:3px solid ${typeColor}">
          <div class="card-hdr">
            <span class="type-label" style="color:${typeColor}">${esc(
          r.noteType
        )}</span>
            <span class="date">${esc(date)}</span>
          </div>
          <div class="title">${esc(getRecordTitle(r))}</div>
          ${details ? `<div class="card-body">${details}</div>` : ''}
        </div>`;
      })
      .join('');

    win.document.write(
      `<html><head><title>Clinical Records${
        patientLabel ? ' - ' + patientLabel.replace(/\//g, '-') : ''
      }</title>
      <style>
        body{font-family:sans-serif;padding:20px;color:#333}
        h2{margin:0 0 4px}
        .meta{margin:0 0 16px;color:#666;font-size:13px}
        .card{border:1px solid #e0e0e0;border-radius:6px;margin-bottom:10px;overflow:hidden;page-break-inside:avoid}
        .card-hdr{display:flex;align-items:center;justify-content:space-between;padding:8px 12px 0 12px}
        .card.expanded .card-body{border-top:1px solid #e0e0e0}
        .type-label{font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase}
        .title{padding:2px 12px 8px;font-size:13px;font-weight:500}
        .date{font-size:12px;font-weight:700;white-space:nowrap}
        .card-body{padding:8px 12px}
        table.details{border-collapse:collapse;font-size:12px;width:100%}
        table.details td{padding:3px 8px 3px 0;vertical-align:top}
        table.details td.lbl{font-weight:600;color:#555;width:140px;white-space:nowrap}
        table.details td.pre{white-space:pre-wrap}
      </style>
      </head><body>
      <h2>Clinical Records${patientLabel ? ` — ${patientLabel}` : ''}</h2>
      ${cards}
      </body></html>`
    );
    win.document.close();
    win.print();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="clinical-browser-title"
      aria-modal="true"
      slotProps={{
        paper: {
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          flexShrink: 0,
          backgroundColor: theme?.buttonStyle || '#f50057',
          color: theme?.buttonText || '#fff'
        }}
      >
        <Typography
          id="clinical-browser-title"
          variant="h6"
          sx={{ fontWeight: 'bold' }}
        >
          Clinical Records{patientLabel ? ` — ${patientLabel}` : ''}
        </Typography>
        <Box
          sx={{
            flex: 1
          }}
        />
        <Button
          size="small"
          startIcon={<SwapVertIcon />}
          onClick={() =>
            setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))
          }
          variant="outlined"
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': {
              borderColor: '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
        </Button>
        <IconButton
          onClick={handlePrint}
          size="small"
          aria-label="Print"
          sx={{ color: '#fff' }}
        >
          <PrintIcon />
        </IconButton>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close modal"
          sx={{ color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <RecordBrowser
        tiles={tiles}
        isLoading={isLoading}
        dateFormat={dateFormat}
        staffMemberOptions={staffMemberOptions}
        carrier={carrier}
        onCreateRecord={onCreateRecord}
        creatableTypes={creatableTypes}
        active={open}
        facetLayout="rail"
        sortOrder={sortOrder}
        onVisibleChange={handleVisibleChange}
      />
    </Dialog>
  );
};

export default RecordBrowserModal;
